/**
 * The background art precache — after boot settles, quietly pull the vendored chassis art into
 * the browser's ordinary HTTP cache so no portrait pops in late mid-encounter.
 *
 * This changes *when* work happens; it is not licence to fetch more of it. Three consequences of
 * that fence are baked into this file:
 *
 * **1. It is `new Image()`, NOT Cache Storage or a service worker.** The deployed reverse proxy
 * serves `/frames/*` with `Cache-Control: public, max-age=86400`. A day outlives any session and
 * survives a reload, so the ordinary HTTP cache is sufficient and a Cache Storage layer would be
 * strictly more machinery for no behaviour. (An earlier one-hour TTL expired *inside* a four-hour
 * game night, which defeated the point.) **If that TTL is ever lowered, revisit this comment
 * before revisiting the code.**
 *
 * **2. The boot sweep covers `/frames/` only; board tokens are warmed per roster.** The board draws
 * each unit's art through Pixi `Assets` (`routes/home/pixiTokenSprite.ts`), and `/tokens/*` carries
 * the same one-day cache header as `/frames/*`. But the token directory is ~10 MB against a table
 * that flies a handful of chassis, so boot does not queue it: `useRosterArtPrecache` puts the
 * roster's own chassis tokens ({@link prioritizeArtUrls}) at the head of the queue instead.
 *
 * **3. It is a good guest.** The sweep starts on an idle callback rather than a mount effect, caps
 * itself to {@link MAX_IN_FLIGHT} in-flight requests, and refuses to run at all on a metered or
 * slow connection ({@link shouldPrecache}) — a player tethering at the table should not have their
 * bandwidth eaten by chassis art they may never look at.
 *
 * The queue and the gate are pure and injected-loader-driven on purpose ({@link createPrecacher}):
 * ordering, dedupe, the concurrency cap and the bail-out are all testable without a DOM, which is
 * what `assetPrecache.test.ts` exercises. Only {@link startAssetPrecache} touches the browser.
 */
import { frameArtUrl, MIRRORED_FRAME_IDS } from "./mechArt";

/**
 * How many prefetches may be in flight at once. Small on purpose: the sweep is strictly lower
 * priority than anything the user is actually waiting for, and browsers already cap per-host
 * connections — the point of this number is to leave headroom under that cap, not to approach it.
 */
const MAX_IN_FLIGHT = 4;

/** The `navigator.connection` fields this module reads. Not in lib.dom's type surface. */
export type ConnectionInfo = {
  saveData?: boolean;
  effectiveType?: string;
};

/** Connection classes the sweep refuses to run on. */
const SLOW_EFFECTIVE_TYPES: ReadonlySet<string> = new Set(["slow-2g", "2g", "3g"]);

/**
 * Whether a background sweep is welcome on this connection. Absent connection info (Safari and
 * Firefox don't ship the Network Information API) means *yes* — the API is an opt-out signal, and
 * treating "unknown" as slow would disable the feature for most non-Chrome players.
 */
export function shouldPrecache(conn: ConnectionInfo | undefined | null): boolean {
  if (!conn) return true;
  if (conn.saveData === true) return false;
  if (conn.effectiveType !== undefined && SLOW_EFFECTIVE_TYPES.has(conn.effectiveType)) return false;
  return true;
}

export type Precacher = {
  /**
   * Queue `urls`. Already-fetched URLs are dropped; `front: true` puts these at the head of the
   * queue **and drags already-queued ones forward with them**, which is the whole tiering
   * mechanism — see the note in {@link createPrecacher}.
   */
  add: (urls: readonly string[], opts?: { front?: boolean }) => void;
  /** The URLs still waiting, in the order they'll be fetched. Test/diagnostic read. */
  pending: () => readonly string[];
  /** How many fetches are in flight right now. Test/diagnostic read. */
  inFlight: () => number;
};

/**
 * Build a precacher over an injected `load`. One in-flight-capped pump drains a single ordered
 * queue; a failed load is swallowed (a 404 on one frame must not stall the other 34).
 *
 * **Why `front` re-orders rather than just skipping duplicates.** Boot queues the whole corpus,
 * and the roster's own frames are only known later — by which time every one of them is already
 * in the queue. A plain "have I seen this URL?" dedupe would therefore drop the priority hint
 * entirely and the tiering would be a no-op that still reads like a feature. So a `front` add
 * *moves* a still-pending URL to the head instead of ignoring it. URLs already fetched (or in
 * flight) are genuinely done and stay dropped.
 */
export function createPrecacher(deps: { load: (url: string) => Promise<void>; maxInFlight?: number }): Precacher {
  const max = deps.maxInFlight ?? MAX_IN_FLIGHT;
  const pending: string[] = [];
  /** Every URL ever queued — the dedupe set, covering pending, in-flight and finished alike. */
  const known = new Set<string>();
  let inFlight = 0;

  const pump = (): void => {
    while (inFlight < max && pending.length > 0) {
      const url = pending.shift()!;
      inFlight += 1;
      deps
        .load(url)
        .catch(() => {})
        .then(() => {
          inFlight -= 1;
          pump();
        });
    }
  };

  return {
    add(urls, opts) {
      const front = opts?.front === true;
      const batch: string[] = [];
      for (const url of urls) {
        if (!known.has(url)) {
          known.add(url);
          batch.push(url);
          continue;
        }
        if (!front) continue;
        const at = pending.indexOf(url);
        if (at >= 0) {
          pending.splice(at, 1);
          batch.push(url);
        }
      }
      if (batch.length === 0) return;
      if (front) pending.unshift(...batch);
      else pending.push(...batch);
      pump();
    },
    pending: () => [...pending],
    inFlight: () => inFlight,
  };
}

/**
 * Fetch one image into the HTTP cache. Resolves on failure as well as success — this is a
 * best-effort warm-up, and a missing file is not worth propagating anywhere.
 *
 * The `img` reference is held by the promise's closure until one of the handlers fires, which is
 * what keeps a detached, never-inserted `Image` alive long enough to finish loading.
 */
function loadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.decoding = "async";
    img.src = url;
  });
}

/** Run `fn` when the browser is next idle, with a ceiling so a permanently busy tab still sweeps. */
function whenIdle(fn: () => void): void {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
  if (typeof ric === "function") ric(fn, { timeout: 3000 });
  else setTimeout(fn, 1000);
}

/**
 * Run `fn` after the document's `load` event, then on the next idle slot.
 *
 * **The `load` half is insurance against a slow link, and it is unproven here.**
 * `requestIdleCallback` reports that the *main thread* is free, which says nothing about the
 * network: on a slow link the thread can idle while the stylesheet and both font faces are still
 * in flight, and the sweep would then compete with the app's own boot. Waiting for `load` closes
 * that window by construction. On localhost every boot resource lands inside ~20 ms, so `load`
 * precedes the idle callback either way and the gate is worth ~3 ms — it costs nothing and
 * demonstrates nothing there. Don't read the e2e flow's ordering assertion as evidence *for* it.
 */
function whenLoadedAndIdle(fn: () => void): void {
  if (document.readyState === "complete") {
    whenIdle(fn);
    return;
  }
  window.addEventListener("load", () => whenIdle(fn), { once: true });
}

/** The app-wide precacher, or `null` until {@link startAssetPrecache} arms it. */
let precacher: Precacher | null = null;

/**
 * Arm the sweep and queue the vendored corpus. Called once from `main.tsx`, at boot.
 *
 * Boot-safe by construction: the work is deferred to an idle callback, so nothing here competes
 * with first paint, and on a metered/slow connection the whole thing is a no-op — including the
 * priority hint, since {@link prioritizeFrameArt} is a no-op while `precacher` stays null.
 */
export function startAssetPrecache(): void {
  if (precacher !== null) return;
  const conn = (navigator as Navigator & { connection?: ConnectionInfo }).connection;
  if (!shouldPrecache(conn)) return;
  const p = createPrecacher({ load: loadImage });
  precacher = p;
  whenLoadedAndIdle(() => p.add(MIRRORED_FRAME_IDS.map(frameArtUrl)));
}

/**
 * Pull the art for `frameIds` to the head of the queue — the table's own mechs before the other
 * thirty-odd chassis. Safe to call repeatedly (the desktops call it whenever the roster changes)
 * and safe to call before {@link startAssetPrecache}, or when the sweep declined to run at all.
 */
export function prioritizeFrameArt(frameIds: readonly string[]): void {
  if (precacher === null || frameIds.length === 0) return;
  precacher.add(frameIds.map(frameArtUrl), { front: true });
}

/**
 * Same, for art that is already a complete URL rather than a frame id — the per-world portrait
 * **uploads** (`/api/uploads/<category>/<id>.<ext>?v=…`), which are named directly by roster state
 * (`PilotData.image` / `MechData.image`) and so never go through {@link frameArtUrl}.
 *
 * These go ahead of the vendored frames on purpose: an uploaded portrait is art this table chose,
 * and there are a handful of them against 35 stock chassis. Callers add them after the frames so
 * the uploads end up at the very head (each `front` add unshifts).
 *
 * Only worth doing because uploads are cacheable: the server sends
 * `Cache-Control: public, max-age=86400` and the client stores a `?v=` token so a re-upload moves
 * the URL. Warming an upload route with no cache headers would re-download every one.
 */
export function prioritizeArtUrls(urls: readonly string[]): void {
  if (precacher === null || urls.length === 0) return;
  precacher.add(urls, { front: true });
}

/** Reset module state. Tests only — there is no runtime reason to disarm the sweep. */
export function resetAssetPrecacheForTest(): void {
  precacher = null;
}
